<template>
  <div class="py-12 space-y-6">
    <!-- Header : image, titre et prix -->
    <div
      class="rounded-2xl border border-gray-200 bg-white p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
    >
      <div class="flex items-center gap-4">
        <div class="shimmerTarget w-32 h-32 rounded-xl overflow-hidden border">
          <ImgComponent
            :src="dishData?.principalImageUrl"
            fallback="/images/logo.png"
            alt="Image du plat"
            class="w-full h-full object-cover"
          />
        </div>
        <div>
          <h1 class="text-3xl font-bold title-color shimmerTarget">
            {{ dishData?.title }}
          </h1>
          <div class="text-sm text-gray-500 shimmerTarget mt-1">
            {{ dishData?.storeName }}
          </div>
          <div class="text-2xl font-bold text-green-600 shimmerTarget mt-2">
            {{ dishData?.price }} {{ dishData?.currency }}
          </div>
        </div>
      </div>

      <!-- Actions et durée -->
      <div class="flex flex-col sm:items-end gap-4 mt-4 sm:mt-0">
        <div class="text-gray-700 shimmerTarget sm:text-right">
          <div class="text-sm text-gray-500">Temps de préparation</div>
          <div class="font-semibold text-lg">
            {{ dishData?.cookDurationInMin }} min
          </div>
        </div>
        <div class="flex gap-2 w-full sm:w-auto">
          <button
            @click="togglePublish"
            :disabled="loading"
            class="px-4 py-2 rounded-lg transition-colors flex-1 sm:flex-initial text-white font-medium"
            :class="
              dishData?.isPublished
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'bg-green-500 hover:bg-green-600'
            "
          >
            <span
              v-if="loading"
              class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
            ></span>
            {{ dishData?.isPublished ? 'Dépublier' : 'Publier' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Description -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
        Description
      </h3>
      <p class="text-gray-600 shimmerTarget">
        {{ dishData?.description || 'Aucune description disponible' }}
      </p>
    </div>

    <!-- Catégories -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
        Catégories
      </h3>
      <div class="flex flex-wrap gap-2 mt-2">
        <span
          v-if="dishData?.category"
          class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm shimmerTarget"
        >
          {{ dishData?.category }}
        </span>
        <span
          v-if="dishData?.subcategory"
          class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm shimmerTarget"
        >
          {{ dishData?.subcategory }}
        </span>
      </div>
    </div>

    <!-- Options du plat -->
    <div
      v-if="dishData?.options && dishData.options.length > 0"
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
        Options disponibles
      </h3>
      <div class="space-y-4 mt-4">
        <div
          v-for="option in dishData.options"
          :key="option.id"
          class="bg-gray-50 rounded-xl p-4"
        >
          <div class="flex items-center justify-between mb-2">
            <h4 class="font-semibold text-gray-800">{{ option.name }}</h4>
            <span
              v-if="option.required"
              class="px-2 py-1 bg-red-100 text-red-800 rounded text-xs"
            >
              Obligatoire
            </span>
            <span
              v-else
              class="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs"
            >
              Optionnel
            </span>
          </div>
          <div class="text-sm text-gray-500 mb-2">
            Max: {{ option.maxChoices }} choix
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div
              v-for="choice in option.choices"
              :key="choice.id"
              class="flex justify-between items-center bg-white rounded-lg p-2 border"
            >
              <span class="text-sm">{{ choice.name }}</span>
              <span class="font-semibold text-green-600 text-sm">
                +{{ choice.amount }} {{ dishData?.currency }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Accompagnements -->
    <div
      v-if="dishData?.accompaniements && dishData.accompaniements.length > 0"
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
        Accompagnements disponibles
      </h3>
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mt-4">
        <div
          v-for="acc in dishData.accompaniements"
          :key="acc.id"
          class="bg-gray-50 rounded-xl p-3 text-center"
        >
          <ImgComponent
            :src="acc.image"
            fallback="/images/logo.png"
            :alt="acc.name"
            class="w-16 h-16 mx-auto rounded-lg object-cover mb-2"
          />
          <div class="text-sm font-medium text-gray-800">{{ acc.name }}</div>
          <div class="text-sm text-green-600 font-semibold">
            +{{ acc.amount || acc.price || '-' }} {{ dishData?.currency }}
          </div>
        </div>
      </div>
    </div>

    <!-- Images supplémentaires -->
    <div
      v-if="
        dishData?.additionalImageUrl && dishData.additionalImageUrl.length > 0
      "
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
        Images supplémentaires
      </h3>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
        <ImgComponent
          v-for="(img, idx) in dishData.additionalImageUrl"
          :key="idx"
          :src="img"
          fallback="/images/logo.png"
          class="w-full h-40 rounded-xl object-cover"
        />
      </div>
    </div>

    <!-- Informations du restaurant -->
    <div
      v-if="dishData?.metadata && false"
      class="rounded-2xl border border-gray-200 bg-white p-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
        Informations du restaurant
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div v-if="dishData.metadata.storeAddress" class="shimmerTarget">
          <div class="text-sm text-gray-500">Adresse</div>
          <div class="font-medium">{{ dishData.metadata.storeAddress }}</div>
        </div>
        <div v-if="dishData.metadata.storeCity" class="shimmerTarget">
          <div class="text-sm text-gray-500">Ville</div>
          <div class="font-medium">{{ dishData.metadata.storeCity }}</div>
        </div>
        <div
          v-if="dishData.metadata.storeLng && dishData.metadata.storeLat"
          class="shimmerTarget sm:col-span-2"
        >
          <div class="text-sm text-gray-500">Localisation</div>
          <div class="text-sm font-mono mt-1">
            {{ dishData.metadata.storeLat?.toFixed(6) }},{{
              dishData.metadata.storeLng?.toFixed(6)
            }}
          </div>
          <iframe
            v-if="dishData.metadata.storeLat && dishData.metadata.storeLng"
            class="w-full h-48 rounded-xl border mt-2"
            :src="`https://www.google.com/maps?q=${dishData.metadata.storeLat},${dishData.metadata.storeLng}&hl=fr&z=15&output=embed`"
          ></iframe>
        </div>
      </div>
    </div>

    <!-- IDs techniques -->
    <div class="rounded-2xl border border-gray-200 bg-gray-50 p-6">
      <h3 class="text-lg font-semibold text-gray-800 shimmerTarget">
        Informations techniques
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div class="shimmerTarget">
          <div class="text-sm text-gray-500">ID du plat</div>
          <div class="font-mono text-xs bg-white rounded p-2 mt-1">
            {{ dishData?.id }}
          </div>
        </div>
        <div class="shimmerTarget">
          <div class="text-sm text-gray-500">ID du restaurant</div>
          <div class="font-mono text-xs bg-white rounded p-2 mt-1">
            {{ dishData?.storeId }}
          </div>
        </div>
        <div v-if="dishData?.categoryId" class="shimmerTarget">
          <div class="text-sm text-gray-500">ID catégorie</div>
          <div class="font-mono text-xs bg-white rounded p-2 mt-1">
            {{ dishData.categoryId }}
          </div>
        </div>
        <div v-if="dishData?.subcategoryId" class="shimmerTarget">
          <div class="text-sm text-gray-500">ID sous-catégorie</div>
          <div class="font-mono text-xs bg-white rounded p-2 mt-1">
            {{ dishData.subcategoryId }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import ApiService from '@/shared/utils/apiService'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import Modal from '@/shared/utils/modal'

const props = defineProps({
  dishData: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['updated'])

const loading = ref(false)

async function togglePublish() {
  const isPublishing = !props.dishData.isPublished
  const action = isPublishing ? 'publish' : 'unpublish'
  const actionText = isPublishing ? 'publier' : 'dépublier'

  const confirm = await Modal.confirm(
    'Confirmation',
    `Voulez-vous vraiment ${actionText} ce plat ?`,
  )
  if (!confirm) return

  loading.value = true
  try {
    const res = await ApiService.patch(
      `dish/${action}/${props.dishData.id}?group=ultraxeno`,
    )
    if (res.success) {
      Events.dispatch(EVENTS.NOTIFICATION, {
        type: 'success',
        message: `Plat ${isPublishing ? 'publié' : 'dépublié'} avec succès`,
      })
      emit('updated')
    }
  } catch (err) {
    Modal.show('Erreur', err.message || `Impossible de ${actionText} le plat`)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.title-color {
  color: #1f2937;
}
</style>
